import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import { apiClient } from '@api';
import { AssessmentStatus } from '@libs/constants';
import {
  CompareVersionsResponse,
  GetAssessmentVersionHistoryResponse,
} from '@libs/types';

import { queryKeys } from '../../../constants/query-keys';

// ============================================================================
// SERVICE FUNCTIONS - Versioning
// ============================================================================

interface GetAssessmentVersionHistoryParams {
  assessmentId: string;
  page?: number;
  limit?: number;
  status?: AssessmentStatus;
}

async function getAssessmentVersionHistory(
  { assessmentId, page, limit, status }: GetAssessmentVersionHistoryParams,
  signal?: AbortSignal,
): Promise<GetAssessmentVersionHistoryResponse> {
  const queryParams = new URLSearchParams();
  if (page) queryParams.append('page', page.toString());
  if (limit) queryParams.append('limit', limit.toString());
  if (status) queryParams.append('status', status);

  const response = await apiClient.get<GetAssessmentVersionHistoryResponse>(
    `api/assessments/${assessmentId}/versions`,
    { params: queryParams, signal },
  );

  return response.data;
}

interface CompareVersionsParams {
  baselineAssessmentId: string;
  comparisonAssessmentId: string;
}

async function compareAssessmentVersions(
  { baselineAssessmentId, comparisonAssessmentId }: CompareVersionsParams,
  signal?: AbortSignal,
): Promise<CompareVersionsResponse> {
  const queryParams = new URLSearchParams();
  queryParams.append('baselineAssessmentId', baselineAssessmentId);
  queryParams.append('comparisonAssessmentId', comparisonAssessmentId);

  const response = await apiClient.get<CompareVersionsResponse>(
    'api/assessments/compare',
    { params: queryParams, signal },
  );

  return response.data;
}

// ============================================================================
// REACT QUERY HOOKS
// ============================================================================

/**
 * Returns every version in the same family as the given assessment, newest-first.
 * Pass any version's ID — the endpoint resolves the root and returns siblings.
 */
export const useGetAssessmentVersionHistory = (
  assessmentId: string | undefined,
  options?: { status?: AssessmentStatus },
  queryOptions?: Omit<UseQueryOptions<GetAssessmentVersionHistoryResponse>, 'queryKey' | 'queryFn'>,
) =>
  useQuery<GetAssessmentVersionHistoryResponse>({
    queryKey: [...queryKeys.assessments.versionHistory(assessmentId ?? ''), options?.status ?? 'all'],
    queryFn: ({ signal }) =>
      getAssessmentVersionHistory(
        { assessmentId: assessmentId!, page: 1, limit: 50, status: options?.status },
        signal,
      ),
    enabled: !!assessmentId,
    ...queryOptions,
  });

/**
 * Compares two versions of the same assessment family.
 * Both IDs must belong to completed assessments in the same family.
 */
export const useGetAssessmentCompareVersions = (
  baselineAssessmentId: string | undefined,
  comparisonAssessmentId: string | undefined,
  queryOptions?: Omit<UseQueryOptions<CompareVersionsResponse>, 'queryKey' | 'queryFn'>,
) =>
  useQuery<CompareVersionsResponse>({
    queryKey: queryKeys.assessments.compare(baselineAssessmentId ?? '', comparisonAssessmentId ?? ''),
    queryFn: ({ signal }) =>
      compareAssessmentVersions(
        {
          baselineAssessmentId: baselineAssessmentId!,
          comparisonAssessmentId: comparisonAssessmentId!,
        },
        signal,
      ),
    enabled: !!baselineAssessmentId && !!comparisonAssessmentId && baselineAssessmentId !== comparisonAssessmentId,
    ...queryOptions,
  });
